import { useMemo } from 'react'

import { theme } from '@hhgtech/hhg-components'
import dayjs from 'dayjs'
import * as yup from 'yup'

import { OvulationCalculatorType } from 'components/templates/ovulationCalculator'
import {
  DAY_JS_DATE_FORMAT,
  OVUL_CALC_TARGET_AVOID_PREGNANCY,
  OVUL_CALC_TARGET_TRACK_PERIOD,
} from 'interfaces/constants'
import { CalendarMarker, CalendarRange } from 'interfaces/types'

import { OvulationFormField } from './context'
import { QUERY_HEALTH_TOOL } from '@hhgtech/hhg-components/constants'

export const getOvulationResultLink = (
  baseUrl: string,
  { type, startDate, periodLength, cycle }: OvulationFormField,
) =>
  `${baseUrl}?result=1&${QUERY_HEALTH_TOOL.method}=${type}&${
    QUERY_HEALTH_TOOL.day
  }=${dayjs(startDate).format(DAY_JS_DATE_FORMAT)}&${
    QUERY_HEALTH_TOOL.cycle
  }=${cycle}&${QUERY_HEALTH_TOOL.period}=${periodLength}&joincommunity=1`

export const useDrawCalendar = (values: OvulationFormField) => {
  const periodRangeColors = {
    color: theme.colors.pink800,
    backgroundColor: theme.colors.pink50,
  }

  const fertileRangeColors = {
    color:
      values.type === OVUL_CALC_TARGET_AVOID_PREGNANCY
        ? theme.colors.error
        : theme.colors.green700,
    backgroundColor: theme.colors.green50,
  }

  const periodRanges = useMemo<CalendarRange[]>(() => {
    const ranges: CalendarRange[] = []
    if (values) {
      const { cycle, periodLength, startDate: periodFirstDay } = values
      const daysToAdd = periodLength - 1
      ranges.push({
        rangeType: 'period',
        ...periodRangeColors,
        startDate: periodFirstDay,
        endDate: dayjs(periodFirstDay).add(daysToAdd, 'day').toDate(),
      })
      let before = periodFirstDay
      let after = periodFirstDay
      for (let i = 0; i <= 12; i++) {
        before = dayjs(before).subtract(cycle, 'day').toDate()
        after = dayjs(after).add(cycle, 'day').toDate()
        ranges.unshift({
          rangeType: 'period',
          ...periodRangeColors,
          startDate: before,
          endDate: dayjs(before).add(daysToAdd, 'day').toDate(),
        })
        ranges.push({
          rangeType: 'period',
          ...periodRangeColors,
          startDate: after,
          endDate: dayjs(after).add(daysToAdd, 'day').toDate(),
        })
      }
    }
    return ranges
  }, [values])

  const ovulationDays = useMemo<Date[]>(() => {
    return periodRanges.map((r) =>
      dayjs(r.startDate).subtract(14, 'day').toDate(),
    )
  }, [periodRanges])

  const ovulationMarkers = useMemo<CalendarMarker[]>(() => {
    return ovulationDays.map((d) => ({
      markerType: 'ovulation',
      markerStyle: 'dashed',
      date: d,
      color: fertileRangeColors.color,
    }))
  }, [ovulationDays, fertileRangeColors])

  const fertileRanges = useMemo<CalendarRange[]>(() => {
    return ovulationDays.map((d) => ({
      rangeType: 'fertile',
      startDate: dayjs(d).subtract(5, 'day').toDate(),
      endDate: dayjs(d).add(1, 'day').toDate(),
      ...fertileRangeColors,
    }))
  }, [ovulationDays, fertileRangeColors])

  const calendarRanges = useMemo(() => {
    if (values.type === OVUL_CALC_TARGET_TRACK_PERIOD) {
      return periodRanges
    }
    return periodRanges.concat(fertileRanges)
  }, [values, periodRanges, fertileRanges])

  return {
    ovulationMarkers,
    calendarRanges,
    ovulationDays,
  }
}

export const generateSchema = () =>
  // f: IntlFormatters['formatMessage']
  {
    return yup.object().shape({
      type: yup
        .mixed<OvulationCalculatorType>()
        .oneOf(Object.values(OvulationCalculatorType) as number[])
        .default(OvulationCalculatorType.TrackPeriod)
        .required(),
      cycle: yup.number().required(),
      periodLength: yup.number().required(),
      startDate: yup.date().required(),
    })
  }
